import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Database, 
  Eye, 
  Lock,
  Cookie,
  Share2,
  Baby,
  Mail,
  Globe,
  Server,
  UserCheck,
  Bell,
  FileText,
  Smartphone,
  MapPin,
  Clock
} from 'lucide-react';

export const Privacy = () => {
  return (
    <div className="bg-zinc-950 min-h-screen pt-20">
      {/* Hero */}
      <section className="py-32 relative overflow-hidden" data-testid="privacy-hero">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[150px]" />
        
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-emerald-500" />
              </div>
              <span className="overline">Legal</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8 font-['Outfit'] tracking-tighter">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl">
              Your privacy is critically important to us. This comprehensive policy explains how HealthCheck Systems 
              collects, uses, maintains, and discloses information collected from users.
            </p>
            <p className="text-zinc-600 text-sm mt-6">
              Last Updated: January 1, 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 border-t border-white/5">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8 border-l-2 border-l-emerald-500"
          >
            <h2 className="text-xl font-bold text-white mb-4 font-['Outfit']">Introduction</h2>
            <div className="text-zinc-400 leading-relaxed space-y-4 text-sm">
              <p>
                HealthCheck Systems ("Company," "we," "us," or "our") respects your privacy and is committed to protecting 
                your personal information. This Privacy Policy describes our policies and procedures on the collection, use, 
                disclosure, and protection of your information when you use our website (healthchecksolutions.online), 
                mobile applications, and services (collectively, the "Services").
              </p>
              <p>
                By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound 
                by this Privacy Policy. If you do not agree to this Privacy Policy, please do not access or use our Services.
              </p>
              <p>
                For information about how we handle Protected Health Information under HIPAA, please see our 
                <Link to="/hipaa" className="text-emerald-500 hover:underline ml-1">Notice of Privacy Practices</Link>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Information We Collect */}
      <section className="py-16 bg-zinc-900/30" data-testid="privacy-collect">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">Information We Collect</h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                icon: UserCheck,
                title: 'Information You Provide to Us',
                items: [
                  'Contact Information: Name, email address, postal address, phone number',
                  'Account Information: Username, password, account preferences',
                  'Company Information: Company name, job title, industry, company size',
                  'Payment Information: Credit card numbers, billing address (processed by secure third-party payment processors)',
                  'Health Information: Medical history, screening results, health assessments (governed by HIPAA)',
                  'Communications: Messages, feedback, survey responses, support requests',
                  'Form Submissions: Information provided through contact forms, screening requests, and appointment scheduling'
                ]
              },
              {
                icon: Server,
                title: 'Information Collected Automatically',
                items: [
                  'Device Information: IP address, device type, operating system, browser type and version',
                  'Usage Information: Pages visited, links clicked, time spent on pages, referring URL',
                  'Location Information: General location based on IP address',
                  'Log Data: Server logs, error reports, access times, pages viewed',
                  'Analytics Data: User interactions, feature usage, performance metrics'
                ]
              },
              {
                icon: Cookie,
                title: 'Information from Cookies and Similar Technologies',
                items: [
                  'Essential Cookies: Required for basic website functionality and security',
                  'Performance Cookies: Help us understand how visitors interact with our website',
                  'Functional Cookies: Remember your preferences and settings',
                  'Analytics Cookies: Collect anonymous data about website usage patterns',
                  'Web Beacons: Small graphics that track user behavior in emails and on web pages',
                  'Local Storage: Store data locally on your device for improved performance'
                ]
              },
              {
                icon: Share2,
                title: 'Information from Third Parties',
                items: [
                  'Healthcare Providers: Information shared by providers involved in your care',
                  'Insurance Companies: Coverage and claims information',
                  'Business Partners: Information from companies we partner with',
                  'Public Sources: Publicly available information',
                  'Social Media: Information from social media platforms if you connect your account'
                ]
              }
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="card-surface rounded-2xl p-8"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-emerald-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-white font-['Outfit']">{section.title}</h3>
                </div>
                <ul className="space-y-2">
                  {section.items.map((item, i) => (
                    <li key={i} className="text-zinc-400 text-sm flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Use Information */}
      <section className="py-16" data-testid="privacy-use">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">How We Use Your Information</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Provide Services', desc: 'Deliver health screening services, process appointments, and provide results' },
              { title: 'Communication', desc: 'Send appointment reminders, service updates, and respond to inquiries' },
              { title: 'Payment Processing', desc: 'Process payments, send invoices, and manage billing' },
              { title: 'Personalization', desc: 'Customize your experience and provide relevant recommendations' },
              { title: 'Analytics', desc: 'Analyze usage patterns to improve our services and user experience' },
              { title: 'Security', desc: 'Detect, prevent, and respond to security incidents and fraud' },
              { title: 'Legal Compliance', desc: 'Comply with applicable laws, regulations, and legal processes' },
              { title: 'Research', desc: 'Conduct research to improve healthcare outcomes (with appropriate consent)' },
              { title: 'Marketing', desc: 'Send promotional materials about our services (with your consent)' },
              { title: 'Quality Assurance', desc: 'Monitor and improve the quality of our services' }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                className="bg-zinc-900/50 rounded-xl p-6 border border-white/5"
              >
                <h4 className="text-white font-medium mb-2">{item.title}</h4>
                <p className="text-zinc-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Information Sharing */}
      <section className="py-16 bg-zinc-900/30" data-testid="privacy-sharing">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">How We Share Your Information</h2>
            <p className="text-zinc-400 text-sm">
              We do not sell your personal information. We may share your information in the following circumstances:
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                title: 'Service Providers',
                content: 'We share information with third-party vendors who perform services on our behalf, such as payment processing, data hosting, analytics, customer service, and marketing. These providers are contractually obligated to protect your information and use it only for the services they provide to us.'
              },
              {
                title: 'Healthcare Providers',
                content: 'We may share your health information with healthcare providers involved in your care, as permitted by HIPAA. This includes physicians, laboratories, and other healthcare facilities that may need access to your screening results for treatment purposes.'
              },
              {
                title: 'Business Partners',
                content: 'We may share information with business partners to offer you certain products, services, or promotions. We require these partners to honor this Privacy Policy and protect your information.'
              },
              {
                title: 'Corporate Transactions',
                content: 'If we are involved in a merger, acquisition, financing, reorganization, bankruptcy, or sale of company assets, your information may be transferred as part of that transaction. We will notify you of any change in ownership or uses of your personal information.'
              },
              {
                title: 'Legal Requirements',
                content: 'We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency). We may also disclose information to protect our rights, privacy, safety, or property, or that of our users or the public.'
              },
              {
                title: 'With Your Consent',
                content: 'We may share your information for other purposes with your explicit consent or at your direction.'
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="card-surface rounded-2xl p-8"
              >
                <h3 className="text-xl font-semibold text-white mb-4 font-['Outfit']">{item.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Security */}
      <section className="py-16" data-testid="privacy-security">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit']">Data Security</h2>
            </div>
            <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
              <p>
                We implement comprehensive security measures to protect your personal information:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Encryption:</strong> All data is encrypted using 256-bit SSL/TLS encryption during transmission and AES-256 encryption at rest</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Access Controls:</strong> Role-based access controls and multi-factor authentication for all staff</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Network Security:</strong> Firewalls, intrusion detection systems, and regular security monitoring</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Physical Security:</strong> Secure data centers with 24/7 monitoring and restricted access</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Regular Audits:</strong> Annual security assessments, penetration testing, and SOC 2 Type II certification</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Employee Training:</strong> Mandatory security awareness training for all employees</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Incident Response:</strong> Documented procedures for responding to security incidents</span>
                </li>
              </ul>
              <p>
                While we strive to protect your personal information, no method of transmission over the Internet or 
                electronic storage is 100% secure. We cannot guarantee absolute security but are committed to protecting 
                your data to the fullest extent possible.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Data Retention */}
      <section className="py-16 bg-zinc-900/30" data-testid="privacy-retention">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit']">Data Retention</h2>
            </div>
            <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this 
                Privacy Policy, unless a longer retention period is required or permitted by law. The criteria used to 
                determine our retention periods include:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span>The length of time we have an ongoing relationship with you and provide services</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span>Legal obligations that require us to retain data for certain periods</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span>Statute of limitations, litigation, or regulatory investigations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span>Medical records retention requirements under applicable healthcare laws</span>
                </li>
              </ul>
              <p>
                Health records are generally retained for a minimum of seven (7) years from the date of service, or longer 
                as required by applicable state or federal law.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Your Rights */}
      <section className="py-16" data-testid="privacy-rights">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">Your Privacy Rights</h2>
            <p className="text-zinc-400 text-sm">
              Depending on your location, you may have certain rights regarding your personal information:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Right to Access', desc: 'Request a copy of the personal information we hold about you' },
              { title: 'Right to Rectification', desc: 'Request correction of inaccurate or incomplete personal information' },
              { title: 'Right to Erasure', desc: 'Request deletion of your personal information in certain circumstances' },
              { title: 'Right to Restrict Processing', desc: 'Request that we limit how we use your personal information' },
              { title: 'Right to Data Portability', desc: 'Receive your personal information in a structured, commonly used format' },
              { title: 'Right to Object', desc: 'Object to processing of your personal information for certain purposes' },
              { title: 'Right to Withdraw Consent', desc: 'Withdraw consent where processing is based on consent' },
              { title: 'Right to Non-Discrimination', desc: 'Exercise your rights without discriminatory treatment' }
            ].map((right, index) => (
              <motion.div
                key={right.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                className="bg-zinc-900/50 rounded-xl p-6 border border-white/5"
              >
                <h4 className="text-white font-medium mb-2">{right.title}</h4>
                <p className="text-zinc-500 text-sm">{right.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6"
          >
            <p className="text-zinc-400 text-sm">
              To exercise any of these rights, please contact us at <span className="text-emerald-500">privacy@healthchecksolutions.online</span>. 
              We will respond to your request within 30 days. We may need to verify your identity before processing your request.
            </p>
          </motion.div>
        </div>
      </section>

      {/* California Privacy Rights */}
      <section className="py-16 bg-zinc-900/30" data-testid="privacy-ccpa">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit']">California Privacy Rights (CCPA/CPRA)</h2>
            </div>
            <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
              <p>
                If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA) 
                and California Privacy Rights Act (CPRA):
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Right to Know:</strong> Request disclosure of the categories and specific pieces of personal information we have collected</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Right to Delete:</strong> Request deletion of personal information we have collected</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Right to Opt-Out:</strong> Opt out of the sale or sharing of personal information (Note: We do not sell personal information)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Right to Correct:</strong> Request correction of inaccurate personal information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">•</span>
                  <span><strong className="text-white">Right to Limit:</strong> Limit the use of sensitive personal information</span>
                </li>
              </ul>
              <p>
                To exercise these rights, contact us at (855) 831-2075 or privacy@healthchecksolutions.online. 
                You may also designate an authorized agent to make a request on your behalf.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Children's Privacy */}
      <section className="py-16" data-testid="privacy-children">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Baby className="w-5 h-5 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit']">Children's Privacy</h2>
            </div>
            <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
              <p>
                Our Services are not directed to children under the age of 13, and we do not knowingly collect personal 
                information from children under 13 without parental consent. If you are a parent or guardian and believe 
                that your child has provided us with personal information without your consent, please contact us immediately.
              </p>
              <p>
                For minors between 13 and 18 years of age, parental or guardian consent may be required for certain services, 
                particularly those involving health information. Parents and guardians have the right to review, correct, 
                or request deletion of their child's personal information.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* International Transfers */}
      <section className="py-16 bg-zinc-900/30" data-testid="privacy-international">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Globe className="w-5 h-5 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit']">International Data Transfers</h2>
            </div>
            <div className="text-zinc-400 text-sm leading-relaxed">
              <p>
                Your personal information may be transferred to and processed in countries other than your country of residence. 
                These countries may have different data protection laws. When we transfer your information, we take appropriate 
                safeguards to ensure your information remains protected in accordance with this Privacy Policy and applicable law. 
                We use standard contractual clauses approved by relevant authorities and other appropriate legal mechanisms for 
                international data transfers.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16" data-testid="privacy-contact">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit']">Contact Us</h2>
            </div>
            <p className="text-zinc-400 mb-6 text-sm">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <div className="text-zinc-500 text-sm space-y-1">
              <p><strong className="text-white">HealthCheck Systems</strong></p>
              <p>Privacy Officer</p>
              <p>901 S Central Expy Ste 100</p>
              <p>Richardson, TX 75080</p>
              <p>Email: privacy@healthchecksolutions.online</p>
              <p>Phone: (855) 831-2075</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Changes */}
      <section className="py-8 border-t border-white/5">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <p className="text-zinc-600 text-sm">
            We may update this Privacy Policy from time to time. We will notify you of material changes by posting 
            the updated policy on our website and updating the "Last Updated" date. Your continued use of our Services 
            after such changes constitutes your acceptance of the updated Privacy Policy.
          </p>
        </div>
      </section>
    </div>
  );
};
